import { Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { useFieldArray } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import {
  DispositiveProvisionDispositionKind,
  DispositiveProvisionTemplateKind,
  DispositiveProvisionTransferTaxKind,
} from '@/types/schema';

import { DISPOSITIVE_PROVISIONS_FORM_NAMESPACE } from '../../DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import {
  DispositiveProvisionsFormPaths,
  DispositiveProvisionsFormRecipient,
  DispositiveProvisionsTemplateSlotKind,
} from '../../DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { DispositiveProvisionsTemplateEditorTabHeader } from '../DispositiveProvisionsTemplateSplitScreenModal.components';
import { DispositiveProvisionsTemplateShape } from '../DispositiveProvisionsTemplateSplitScreenModal.types';
import { TemplateRecipientSlot } from './DispositiveProvisionsTemplateSplitScreenModal.components';

export function DispositiveProvisionsTemplateABCTrustPreset() {
  const { control } = useFormContext<DispositiveProvisionsTemplateShape>();
  const {
    append,
    remove,
    fields: recipientFields,
  } = useFieldArray({
    control,
    name: `${DISPOSITIVE_PROVISIONS_FORM_NAMESPACE}.recipients` as const satisfies DispositiveProvisionsFormPaths,
    keyName: '_id',
  });

  return (
    <Stack direction="column" spacing={2}>
      <DispositiveProvisionsTemplateEditorTabHeader
        templateType={DispositiveProvisionTemplateKind.AbcTrust}
        recipients={recipientFields}
        onAddTemplateRecipient={(value, options) => append(value, options)}
      />
      {recipientFields.map((recipient, index) => (
        <TemplateRecipientSlot
          key={recipient._id}
          index={index}
          slotKind={recipient._templateSlotKind}
          onRemove={() => remove(index)}
        />
      ))}
    </Stack>
  );
}

function getBlankCreditShelter(): DispositiveProvisionsFormRecipient {
  return {
    _templateSlotKind: DispositiveProvisionsTemplateSlotKind.AbcCreditShelter,
    _dispositiveProvisionId: null,
    assetGroupIds: [],
    assetStaticIds: [],
    insurancePolicyIds: [],
    ownershipStakeIds: [],
    dispositionAssetPercentage: null,
    recipientId: '',
    recipientKind: null,
    dispositionKind_ButtonGroup: 'other',
    dispositionKind_Select:
      DispositiveProvisionDispositionKind.LesserOfRemainingStateOrFederalExemption,
    dispositionAmount: null,
    dispositionPercentage: null,
    notes: '',
    transferTaxKind: DispositiveProvisionTransferTaxKind.SpouseCreditShelter,
  };
}

const BLANK_ABC_FED_EXEMPT_STATE_NON_EXEMPT_TRUST: DispositiveProvisionsFormRecipient =
  {
    _templateSlotKind:
      DispositiveProvisionsTemplateSlotKind.AbcFederalExemptStateNonExempt,
    _dispositiveProvisionId: null,
    assetGroupIds: [],
    assetStaticIds: [],
    insurancePolicyIds: [],
    ownershipStakeIds: [],
    dispositionAssetPercentage: null,
    recipientId: '',
    recipientKind: null,
    dispositionKind_ButtonGroup: 'other',
    dispositionKind_Select:
      DispositiveProvisionDispositionKind.RemainingFederalLifetimeExemptionInExcessOfStateExemption,
    dispositionAmount: null,
    dispositionPercentage: new Decimal(100),
    notes: '',
    transferTaxKind:
      DispositiveProvisionTransferTaxKind.SpouseFederalCreditShelterStateMaritalExclusion,
  };

const BLANK_ABC_GST_EXEMPT_MARITAL_TRUST: DispositiveProvisionsFormRecipient = {
  _templateSlotKind: DispositiveProvisionsTemplateSlotKind.AbcGstExempt,
  _dispositiveProvisionId: null,
  assetGroupIds: [],
  assetStaticIds: [],
  insurancePolicyIds: [],
  ownershipStakeIds: [],
  dispositionAssetPercentage: null,
  recipientId: '',
  recipientKind: null,
  dispositionKind_ButtonGroup: 'other',
  dispositionKind_Select:
    DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantorInExcessOfLifetimeExclusion,
  dispositionAmount: null,
  dispositionPercentage: new Decimal(100),
  notes: '',
  transferTaxKind: DispositiveProvisionTransferTaxKind.SpouseMaritalExclusion,
};

const BLANK_ABC_GST_NON_EXEMPT_MARITAL_TRUST: DispositiveProvisionsFormRecipient =
  {
    _templateSlotKind: DispositiveProvisionsTemplateSlotKind.AbcGstNonExempt,
    _dispositiveProvisionId: null,
    assetGroupIds: [],
    assetStaticIds: [],
    insurancePolicyIds: [],
    ownershipStakeIds: [],
    dispositionAssetPercentage: null,
    recipientId: '',
    recipientKind: null,
    dispositionKind_ButtonGroup: 'other',
    dispositionKind_Select:
      DispositiveProvisionDispositionKind.AnythingLeftOver,
    dispositionAmount: null,
    dispositionPercentage: new Decimal(100),
    notes: '',
    transferTaxKind: DispositiveProvisionTransferTaxKind.SpouseMaritalExclusion,
  };
export const getDefaultAbcTrustRecipients: () => DispositiveProvisionsFormRecipient[] =
  () => [
    getBlankCreditShelter(),
    BLANK_ABC_FED_EXEMPT_STATE_NON_EXEMPT_TRUST,
    BLANK_ABC_GST_EXEMPT_MARITAL_TRUST,
    BLANK_ABC_GST_NON_EXEMPT_MARITAL_TRUST,
  ];
